<template>
    <div class="faq-page">
        <div class="container">
            <div class="page-header">
                <h1 class="page-title">{{ t('faqPage.title') }}</h1>
                <p class="page-subtitle">{{ t('faqPage.subtitle') }}</p>
                <p class="help-text">
                    {{ t('faqPage.helpText') }}
                    <a href="mailto:service@weryai.com.cn" class="help-link"
                        aria-label="Contact 微鲤AI support via email">service@weryai.com.cn</a>
                    {{ t('faqPage.helpText2') }}
                </p>
            </div>

            <div class="faq-content">
                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.whatIsWeryAI') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>{{ t('faqPage.whatIsWeryAIAnswer') }}</p>
                        <p>
                            {{ t('faqPage.whatIsWeryAIAnswer2') }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.isWeryAISuitableForBeginners') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>
                            {{
                                t('faqPage.isWeryAISuitableForBeginnersAnswer')
                            }}
                        </p>
                        <p>
                            {{
                                t('faqPage.isWeryAISuitableForBeginnersAnswer2')
                            }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.canIUseWeryAIFree') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>
                            {{ t('faqPage.canIUseWeryAIFreeAnswer') }}
                        </p>
                        <p>
                            {{ t('faqPage.canIUseWeryAIFreeAnswer2') }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.paymentMethodsQuestion') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>{{ t('faqPage.paymentMethodsAnswer') }}</p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.howToCancelMySubscription') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>
                            {{ t('faqPage.howToCancelMySubscriptionAnswer') }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.whenWillMySubscriptionRenew') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>
                            {{ t('faqPage.whenWillMySubscriptionRenewAnswer') }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.canIUseWeryAIForCommercialPurposes') }}
                    </h2>
                    <div class="faq-answer_">
                        <p>
                            {{
                                t(
                                    'faqPage.canIUseWeryAIForCommercialPurposesAnswer'
                                )
                            }}
                        </p>
                        <p>
                            {{
                                t(
                                    'faqPage.canIUseWeryAIForCommercialPurposesAnswer2'
                                )
                            }}
                        </p>
                    </div>
                </div>

                <div class="faq-section">
                    <h2 class="faq-question">
                        {{ t('faqPage.refundPolicy') }}
                    </h2>
                    <div class="faq-answer_">
                        <p v-html="t('pricing.refundPolicyAnswer')"></p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { definePageMeta, useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { computed } from 'vue'

const { t } = useI18n()

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'faq' })

// FAQ 数据（与页面内容同步）
const faqData = computed(() => [
    {
        question: t('faqPage.whatIsWeryAI'),
        answer: `${t('faqPage.whatIsWeryAIAnswer')} ${t('faqPage.whatIsWeryAIAnswer2')}`,
    },
    {
        question: t('faqPage.isWeryAISuitableForBeginners'),
        answer: `${t('faqPage.isWeryAISuitableForBeginnersAnswer')} ${t('faqPage.isWeryAISuitableForBeginnersAnswer2')}`,
    },
    {
        question: t('faqPage.canIUseWeryAIFree'),
        answer: `${t('faqPage.canIUseWeryAIFreeAnswer')} ${t('faqPage.canIUseWeryAIFreeAnswer2')}`,
    },
    {
        question: t('faqPage.paymentMethodsQuestion'),
        answer: t('faqPage.paymentMethodsAnswer'),
    },
    {
        question: t('faqPage.howToCancelMySubscription'),
        answer: t('faqPage.howToCancelMySubscriptionAnswer'),
    },
    {
        question: t('faqPage.whenWillMySubscriptionRenew'),
        answer: t('faqPage.whenWillMySubscriptionRenewAnswer'),
    },
    {
        question: t('faqPage.canIUseWeryAIForCommercialPurposes'),
        answer: `${t('faqPage.canIUseWeryAIForCommercialPurposesAnswer')} ${t('faqPage.canIUseWeryAIForCommercialPurposesAnswer2')}`,
    },
    {
        question: t('faqPage.refundPolicy'),
        answer: `${t('faqPage.refundPolicyAnswer')} ${t('faqPage.refundPolicyAnswer2')}`,
    },
])

// 注意：FAQPage 已由 useMultilingualSeo 注入，无需重复调用 useFaqSeo

// BreadcrumbList 结构化数据
const requestUrlHref = useRequestURL().href

useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: '微鲤AI',
                            item: 'https://www.weryai.com.cn',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('faqPage.title'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})
</script>

<style lang="less" scoped>
.faq-page {
    min-height: calc(100vh - 200px);
    padding: 4rem 0;
    background-color: #0d0e12;
    color: white;
}

.container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1rem;
}

/* 页面标题区域 */
.page-header {
    text-align: center;
    margin-bottom: 4rem;
}

.page-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.page-subtitle {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1rem;
}

.help-text {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
}

.help-link {
    color: #60a5fa;
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.2s ease;

    &:hover {
        color: #93c5fd;
        text-decoration: underline;
    }
}

/* FAQ 内容区域 */
.faq-content {
    max-width: 800px;
    margin: 0 auto;
}

.faq-section {
    margin-bottom: 3rem;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        transform: translateY(-2px);
    }
}

.faq-question {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin-bottom: 1.5rem;
    line-height: 1.4;
}

.faq-answer_ {
    font-size: 1rem;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.8);

    p {
        margin-bottom: 1rem;

        &:last-child {
            margin-bottom: 0;
        }
    }

    strong {
        color: #60a5fa;
        font-weight: 600;
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .page-title {
        font-size: 2.5rem;
    }

    .page-subtitle {
        font-size: 1.125rem;
    }

    .faq-question {
        font-size: 1.25rem;
    }

    .faq-section {
        padding: 1.5rem;
        margin-bottom: 2rem;
    }

    .faq-page {
        padding: 2rem 0;
    }
}

@media (max-width: 480px) {
    .page-title {
        font-size: 2rem;
    }

    .container {
        padding: 0 0.75rem;
    }

    .faq-section {
        padding: 1rem;
    }

    .faq-question {
        font-size: 1.125rem;
    }

    .faq-answer_ {
        font-size: 0.9rem;
    }
}
</style>
